import { Entity } from './base-raptor-models';
import { Labor } from './labor.model';
import { Part } from './part.model';

/**
 * The kind of component an AddonItem contributes to an add-on bundle. String enum because the backend
 * serializes AddonItemType as its enum name (JsonStringEnumConverter), not its ordinal.
 */
export enum AddonItemType {
    Part = 'Part',
    Labor = 'Labor',
    Comment = 'Comment',
    Fee = 'Fee',
    Prompt = 'Prompt',
}

/**
 * A single component within an Addon bundle. Depending on type the item references a part, a labor
 * operation, or carries free-text / monetary content (comment, fee, prompt) that is expanded onto a
 * quote as a child line when the owning add-on is applied. Distinct from AppointmentAddOn, which is
 * an unrelated appointment-scheduling concept.
 */
export interface AddonItem extends Entity {
    addonId: string;
    type: AddonItemType;
    partId?: string;
    part?: Part;
    laborId?: string;
    labor?: Labor;
    order: number;
    quantity: number;
    /** When true, quantity is multiplied by the triggering part's quantity rather than applied as a fixed amount. */
    multiplyByParentQuantity: boolean;
    description?: string;
    /** When true, the expanded line is taxable. Sourced from TM ADDON.CHECKTAX. */
    taxable?: boolean;
    /** Monetary value for a Fee item. */
    amount?: number;
    /** Server-resolved per-branch inventory selling price for a Part item. Preferred over the part's catalog list
     * price when pricing the expanded line; absent when the source did not resolve a branch price. */
    sellingPrice?: number;
}

/**
 * A reusable bundle of items (parts, labor, comments, fees, prompts) linked to a Part via Part.addonId.
 * When such a part is placed on a quote, the bundle's items are auto-expanded into additional quote lines.
 */
export interface Addon extends Entity {
    name: string;
    code?: string;
    description?: string;
    items?: AddonItem[];
}
